import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faHome, faArrowRight, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../Shared/Header';
import { decryptData } from '../../utils/encryption';

const PaymentCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // 'checking' | 'success' | 'failed'
  
  useEffect(() => {
    // Fawaterak typically redirects with query parameters indicating success or failure.
    // e.g. ?status=success or ?status=failed
    const paymentStatusRaw = searchParams.get('status') || searchParams.get('payment_status');
    const successParamRaw = searchParams.get('success');
    const invoiceId = searchParams.get('invoice_id');

    // Attempt to decrypt the parameters, fallback to raw value if decryption fails or returns null
    const paymentStatus = decryptData(paymentStatusRaw) ?? paymentStatusRaw;
    const successParam = decryptData(successParamRaw) ?? successParamRaw;

    // Simple heuristic to determine status from URL
    if (paymentStatus === 'success' || paymentStatus === 'paid' || String(successParam) === 'true') {
      setStatus('success');
    } else if (paymentStatus === 'failed' || paymentStatus === 'unpaid' || String(successParam) === 'false') {
      setStatus('failed');
    } else if (invoiceId) {
      // If we got an invoice ID but no explicit failure, we default to success visually.
      // The backend webhook is the source of truth for fulfilling the order anyway.
      setStatus('success'); 
    } else {
      setStatus('failed');
    }
  }, [searchParams]);

  return (
    <div className="pc-root" dir="rtl">
      <Header />
      <div className="pc-container">
        <motion.div 
          className="pc-card"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, type: 'spring' }}
        >
          {status === 'checking' && (
            <div className="pc-checking">جاري التحقق من حالة الدفع...</div>
          )}

          {status === 'success' && (
            <>
              <motion.div 
                className="pc-icon success"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 12, delay: 0.2 }}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
              </motion.div>
              <h1 className="pc-title">تم الدفع بنجاح!</h1>
              <p className="pc-desc">
                لقد تمت عملية الدفع الخاصة بك بنجاح. يمكنك الآن الاستمتاع بكافة مميزات الباقة التي قمت بالاشتراك بها.
              </p>
              <div className="pc-hint">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>يرجى العلم أن تفعيل الباقة يستغرق حتى 10 دقائق.</span>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <motion.div 
                className="pc-icon failed"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 12, delay: 0.2 }}
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </motion.div>
              <h1 className="pc-title">فشلت عملية الدفع</h1>
              <p className="pc-desc">
                عذراً، حدثت مشكلة أثناء إتمام عملية الدفع. يرجى المحاولة مرة أخرى أو اختيار طريقة دفع مختلفة.
              </p>
            </>
          )}

          <div className="pc-actions">
            <button className="pc-btn-primary" onClick={() => navigate('/')}>
              العودة للرئيسية
              <FontAwesomeIcon icon={faHome} />
            </button>
            {status === 'failed' && (
              <button className="pc-btn-secondary" onClick={() => navigate('/pricing')}>
                المحاولة مرة أخرى
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            )}
          </div>
        </motion.div>
      </div>

      <style jsx>{`
        .pc-root {
          min-height: 100vh;
          background: #f8fafc;
          font-family: 'Zain', sans-serif;
        }

        .pc-container {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 100px 20px 40px;
          min-height: calc(100vh - 80px);
        }

        .pc-card {
          background: #fff;
          border-radius: 24px;
          padding: 48px;
          text-align: center;
          max-width: 480px;
          width: 100%;
          box-shadow: 0 20px 40px rgba(0,0,0,0.05);
          border: 1px solid #e2e8f0;
        }

        .pc-icon {
          font-size: 5rem;
          margin-bottom: 24px;
        }

        .pc-icon.success {
          color: #10b981;
        }

        .pc-icon.failed {
          color: #ef4444;
        }

        .pc-title {
          font-size: 2rem;
          font-weight: 800;
          color: #0f172a;
          margin: 0 0 16px;
        }

        .pc-desc {
          color: #64748b;
          font-size: 1.1rem;
          line-height: 1.6;
          margin: 0 0 16px;
        }

        .pc-hint {
          background: #eff6ff;
          color: #2563eb;
          padding: 12px 16px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-weight: 700;
          margin-bottom: 32px;
          border: 1px dashed #bfdbfe;
        }

        .pc-actions {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .pc-btn-primary {
          background: #6366f1;
          color: #fff;
          border: none;
          padding: 16px;
          border-radius: 16px;
          font-size: 1.1rem;
          font-weight: 800;
          font-family: inherit;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: 0.3s;
        }

        .pc-btn-primary:hover {
          background: #4f46e5;
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(99, 102, 241, 0.2);
        }

        .pc-btn-secondary {
          background: transparent;
          color: #6366f1;
          border: 2px solid #e0e7ff;
          padding: 16px;
          border-radius: 16px;
          font-size: 1.1rem;
          font-weight: 800;
          font-family: inherit;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: 0.3s;
        }

        .pc-btn-secondary:hover {
          background: #e0e7ff;
        }

        @media (max-width: 480px) {
          .pc-card {
            padding: 32px 24px;
          }
          .pc-title {
            font-size: 1.6rem;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentCallback;
